import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../api";

/* =====================================================
   ASYNC THUNKS
   ===================================================== */

/**
 * Fetch comprehensive Trust Hub dashboard status
 *
 * INPUT:
 * - userId: User ID
 *
 * OUTPUT:
 * - Dashboard data including profile, voice, SMS, phone inventory status
 */
export const fetchTrustHubDashboard = createAsyncThunk(
  "trustHub/fetchDashboard",
  async (userId, { rejectWithValue }) => {
    try {
      console.log('trustHubSlice - Calling getDashboardStatus for userId:', userId);
      const res = await api.trustHubCentralized.getDashboardStatus(userId);
      console.log('trustHubSlice - API response:', res);

      if (!res || !res.success) {
        console.error('trustHubSlice - API failed:', res);
        return rejectWithValue(res?.message || "Failed to load Trust Hub dashboard");
      }

      console.log('trustHubSlice - Dashboard data:', res.data);
      return res.data;
    } catch (err) {
      console.error("fetchTrustHubDashboard error:", err);
      console.error("Error details:", err.response?.data || err.message);
      return rejectWithValue(err.response?.data?.message || err.message || "Unknown error occurred");
    }
  }
);

/**
 * Fetch Trust Hub business profile
 *
 * INPUT:
 * - userId: User ID
 *
 * OUTPUT:
 * - Profile data
 */
export const fetchTrustHubProfile = createAsyncThunk(
  "trustHub/fetchProfile",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await api.trustHubCentralized.getProfile(userId);
      console.log('fetchTrustHubProfile - API response:', res);

      // If no response at all, that's an error
      if (!res) {
        return rejectWithValue("Failed to load profile - no response");
      }

      // If profile not found (status:false with specific message), return null (not an error)
      if (!res.success && !res.status && res.message && res.message.includes("not found")) {
        console.log('fetchTrustHubProfile - Profile not found, returning null');
        return null;
      }

      // If API returned error for other reasons, reject
      if (!res.success && !res.status) {
        return rejectWithValue(res?.message || "Failed to load profile");
      }

      // Return profile data (handles both {data: ...} and {profile: ...})
      return res.data || res.profile;
    } catch (err) {
      console.error("fetchTrustHubProfile error:", err);
      return rejectWithValue(err.message || "Unknown error occurred");
    }
  }
);

/**
 * Fetch phone inventory
 *
 * INPUT:
 * - userId: User ID
 * - params: Query parameters (search, type, status, per_page)
 *
 * OUTPUT:
 * - Phone inventory data with pagination
 */
export const fetchPhoneInventory = createAsyncThunk(
  "trustHub/fetchPhoneInventory",
  async ({ userId, params = {} }, { rejectWithValue }) => {
    try {
      console.log('fetchPhoneInventory - Request:', { userId, params });
      const res = await api.trustHubCentralized.getUserPhones(userId, params);
      console.log('fetchPhoneInventory - Response:', res);

      if (!res || !res.success) {
        console.error('fetchPhoneInventory - Failed:', res?.message);
        return rejectWithValue(res?.message || "Failed to load phone inventory");
      }

      console.log('fetchPhoneInventory - Phone data:', res.data);
      return res.data;
    } catch (err) {
      console.error("fetchPhoneInventory error:", err);
      return rejectWithValue(err.message || "Unknown error occurred");
    }
  }
);

/**
 * Fetch Voice Compliance status
 *
 * INPUT:
 * - userId: User ID
 *
 * OUTPUT:
 * - CNAM, SHAKEN/STIR, Voice Integrity status
 */
export const fetchVoiceCompliance = createAsyncThunk(
  "trustHub/fetchVoiceCompliance",
  async (userId, { rejectWithValue }) => {
    try {
      const [cnamRes, stirShakenRes, integrityRes] = await Promise.all([
        api.trustHubCentralized.getCNAM(userId),
        api.trustHubCentralized.getStirShaken(userId),
        api.trustHubCentralized.getVoiceIntegrity(userId),
      ]);

      console.log('fetchVoiceCompliance - CNAM response:', cnamRes);
      console.log('fetchVoiceCompliance - STIR/SHAKEN response:', stirShakenRes);
      console.log('fetchVoiceCompliance - Voice Integrity response:', integrityRes);

      return {
        cnam: (cnamRes.success || cnamRes.status) ? cnamRes.data : null,
        shakenStir: (stirShakenRes.success || stirShakenRes.status) ? stirShakenRes.data : null,
        voiceIntegrity: (integrityRes.success || integrityRes.status) ? integrityRes.data : null,
      };
    } catch (err) {
      console.error("fetchVoiceCompliance error:", err);
      return rejectWithValue(err.message || "Unknown error occurred");
    }
  }
);

/**
 * Fetch SMS Compliance (A2P 10DLC) status
 *
 * INPUT:
 * - userId: User ID
 *
 * OUTPUT:
 * - Brand and Campaign full details with phone assignment count
 */
export const fetchSmsCompliance = createAsyncThunk(
  "trustHub/fetchSmsCompliance",
  async (userId, { rejectWithValue }) => {
    try {
      // First, get comprehensive SMS compliance status including phone assignments
      const complianceStatusRes = await api.outbound.getSmsComplianceStatus(userId);
      console.log('fetchSmsCompliance - Compliance status response:', complianceStatusRes);

      // Get A2P status (brand_status, campaign_status, brand_id, campaign_id)
      const statusRes = await api.trustHubCentralized.getA2PStatus(userId);
      console.log('fetchSmsCompliance - Raw status response:', statusRes);

      // Check if we have a response at all
      if (!statusRes) {
        return rejectWithValue("Failed to load SMS compliance - no response");
      }

      // Extract status data - handle both success/failure cases
      // Even if status:false, we might have brand_status/campaign_status in the response
      const statusData = statusRes.data || statusRes || {};
      console.log('fetchSmsCompliance - Status data:', statusData);

      // Extract compliance data (includes phone assignment info)
      const complianceData = complianceStatusRes?.data || {};
      console.log('fetchSmsCompliance - Compliance data:', complianceData);

      // Fetch full brand and campaign details if they exist
      let brandData = null;
      let campaignData = null;

      // Check if we have brand_id or brand_status in status data or response root
      const hasBrand = statusData.brand_id || statusData.brand_status || statusRes.brand_status;
      const hasCampaign = statusData.campaign_id || statusData.campaign_status || statusRes.campaign_status;

      console.log('fetchSmsCompliance - hasBrand:', hasBrand, 'hasCampaign:', hasCampaign);

      // Fetch brand details if brand exists
      if (hasBrand) {
        const brandRes = await api.outbound.getA2PBrandDetails(userId);
        console.log('fetchSmsCompliance - Brand response:', brandRes);
        console.log('fetchSmsCompliance - brandRes.data:', brandRes?.data);
        console.log('fetchSmsCompliance - brandRes.brand_status:', brandRes?.brand_status);

        if (brandRes) {
          // If data exists, use it (this means brand is editable - draft/pending/rejected)
          if (brandRes.data) {
            brandData = brandRes.data;
            console.log('fetchSmsCompliance - Using brandRes.data directly');
          }
          // If brand is approved and can't be edited, construct data from response
          else if (brandRes.brand_status && ['approved', 'twilio-approved', 'verified'].includes(brandRes.brand_status)) {
            console.log('fetchSmsCompliance - Brand is approved, constructing data');
            // Use status data to show the brand exists with approved status
            // IMPORTANT: brandRes.brand_status is the source of truth (most up-to-date)
            brandData = {
              id: statusData.brand_id || brandRes.brand_id || statusRes.brand_id || 1,
              brand_status: brandRes.brand_status,  // Keep original field for backend compatibility
              status: brandRes.brand_status,  // Map to 'status' for component compatibility
              brand_friendly_name: statusData.brand_name || brandRes.brand_name || statusRes.brand_name || statusData.brand_friendly_name || 'Your Brand',
              brand_type: statusData.brand_type || brandRes.brand_type || statusRes.brand_type || 'Business',
              twilio_brand_sid: statusData.twilio_brand_sid || brandRes.twilio_brand_sid || statusRes.twilio_brand_sid,
              ein: statusData.ein || brandRes.ein || statusRes.ein,
              updated_at: statusData.updated_at || brandRes.updated_at || statusRes.updated_at,
            };
            console.log('fetchSmsCompliance - Constructed approved brand data:', brandData);
          }
          // If brand exists with other statuses (draft, pending, rejected)
          else if (brandRes.brand_status) {
            console.log('fetchSmsCompliance - Brand has other status, constructing data');
            brandData = {
              id: statusData.brand_id || brandRes.brand_id || statusRes.brand_id || 1,
              brand_status: brandRes.brand_status,  // Keep original field for backend compatibility
              status: brandRes.brand_status,  // Map to 'status' for component compatibility
              brand_friendly_name: statusData.brand_name || brandRes.brand_name || statusRes.brand_name || statusData.brand_friendly_name,
              brand_type: statusData.brand_type || brandRes.brand_type || statusRes.brand_type,
              twilio_brand_sid: statusData.twilio_brand_sid || brandRes.twilio_brand_sid || statusRes.twilio_brand_sid,
              ein: statusData.ein || brandRes.ein || statusRes.ein,
              updated_at: statusData.updated_at || brandRes.updated_at || statusRes.updated_at,
            };
            console.log('fetchSmsCompliance - Constructed brand data:', brandData);
          }
          console.log('fetchSmsCompliance - Final brand data:', brandData);
        }

        // Fallback: If we still don't have brand data but we know brand exists from statusRes
        if (!brandData && statusRes.brand_status) {
          console.log('fetchSmsCompliance - Using fallback brand data from statusRes');
          brandData = {
            id: statusRes.brand_id || statusData.brand_id || 1,
            brand_status: statusRes.brand_status,
            status: statusRes.brand_status,  // Map to 'status' for component compatibility
            brand_friendly_name: statusRes.brand_name || statusData.brand_name || 'Your Brand',
            brand_type: statusRes.brand_type || statusData.brand_type || 'Business',
            twilio_brand_sid: statusRes.twilio_brand_sid || statusData.twilio_brand_sid,
            ein: statusRes.ein || statusData.ein,
            updated_at: statusRes.updated_at || statusData.updated_at,
          };
          console.log('fetchSmsCompliance - Fallback brand data:', brandData);
        }
      }

      // Fetch campaign details if campaign exists
      if (hasCampaign) {
        const campaignRes = await api.outbound.getA2PCampaignDetails(userId);
        console.log('fetchSmsCompliance - Campaign response:', campaignRes);
        console.log('fetchSmsCompliance - campaignRes.data:', campaignRes?.data);
        console.log('fetchSmsCompliance - campaignRes.campaign_status:', campaignRes?.campaign_status);

        if (campaignRes) {
          // If data exists, use it (this means campaign is editable - draft/pending/rejected)
          if (campaignRes.data) {
            campaignData = campaignRes.data;
            console.log('fetchSmsCompliance - Using campaignRes.data directly');
          }
          // If campaign is approved and can't be edited, construct data from response
          else if (campaignRes.campaign_status && ['approved', 'twilio-approved', 'verified'].includes(campaignRes.campaign_status)) {
            console.log('fetchSmsCompliance - Campaign is approved, constructing data');
            // IMPORTANT: campaignRes.campaign_status is the source of truth (most up-to-date)
            campaignData = {
              id: statusData.campaign_id || campaignRes.campaign_id || statusRes.campaign_id || 1,
              campaign_status: campaignRes.campaign_status,  // Keep original field for backend compatibility
              status: campaignRes.campaign_status,  // Map to 'status' for component compatibility
              campaign_friendly_name: statusData.campaign_name || campaignRes.campaign_name || statusRes.campaign_name || statusData.campaign_friendly_name || 'Your Campaign',
              use_case: statusData.use_case || campaignRes.use_case || statusRes.use_case || 'Marketing',
              message_flow: statusData.message_flow || campaignRes.message_flow || statusRes.message_flow,
              twilio_campaign_sid: statusData.twilio_campaign_sid || campaignRes.twilio_campaign_sid || statusRes.twilio_campaign_sid,
              updated_at: statusData.updated_at || campaignRes.updated_at || statusRes.updated_at,
            };
            console.log('fetchSmsCompliance - Constructed approved campaign data:', campaignData);
          }
          // If campaign exists with other statuses (draft, pending, rejected)
          else if (campaignRes.campaign_status) {
            console.log('fetchSmsCompliance - Campaign has other status, constructing data');
            campaignData = {
              id: statusData.campaign_id || campaignRes.campaign_id || statusRes.campaign_id || 1,
              campaign_status: campaignRes.campaign_status,  // Keep original field for backend compatibility
              status: campaignRes.campaign_status,  // Map to 'status' for component compatibility
              campaign_friendly_name: statusData.campaign_name || campaignRes.campaign_name || statusRes.campaign_name || statusData.campaign_friendly_name,
              use_case: statusData.use_case || campaignRes.use_case || statusRes.use_case,
              message_flow: statusData.message_flow || campaignRes.message_flow || statusRes.message_flow,
              twilio_campaign_sid: statusData.twilio_campaign_sid || campaignRes.twilio_campaign_sid || statusRes.twilio_campaign_sid,
              updated_at: statusData.updated_at || campaignRes.updated_at || statusRes.updated_at,
            };
            console.log('fetchSmsCompliance - Constructed campaign data:', campaignData);
          }
          console.log('fetchSmsCompliance - Final campaign data:', campaignData);
        }

        // Fallback: If we still don't have campaign data but we know campaign exists from statusRes
        if (!campaignData && statusRes.campaign_status) {
          console.log('fetchSmsCompliance - Using fallback campaign data from statusRes');
          campaignData = {
            id: statusRes.campaign_id || statusData.campaign_id || 1,
            campaign_status: statusRes.campaign_status,
            status: statusRes.campaign_status,  // Map to 'status' for component compatibility
            campaign_friendly_name: statusRes.campaign_name || statusData.campaign_name || 'Your Campaign',
            use_case: statusRes.use_case || statusData.use_case || 'Marketing',
            message_flow: statusRes.message_flow || statusData.message_flow,
            twilio_campaign_sid: statusRes.twilio_campaign_sid || statusData.twilio_campaign_sid,
            updated_at: statusRes.updated_at || statusData.updated_at,
          };
          console.log('fetchSmsCompliance - Fallback campaign data:', campaignData);
        }
      }

      return {
        brand: brandData,
        campaign: campaignData,
        phoneAssignments: {
          assignedCount: complianceData.numbers?.assigned_count || 0,
          hasAssigned: complianceData.numbers?.has_assigned || false,
        },
      };
    } catch (err) {
      console.error("fetchSmsCompliance error:", err);
      return rejectWithValue(err.message || "Unknown error occurred");
    }
  }
);

/* =====================================================
   INITIAL STATE
   ===================================================== */

const initialState = {
  /* ---------- Dashboard Data ---------- */
  dashboardData: null,
  dashboardLoading: false,
  dashboardError: null,

  /* ---------- Business Profile ---------- */
  profile: null,
  profileStatus: null,
  profileLoading: false,
  profileError: null,

  /* ---------- Voice Compliance ---------- */
  voiceCompliance: {
    cnam: null,
    shakenStir: null,
    voiceIntegrity: null,
  },
  voiceLoading: false,
  voiceError: null,

  /* ---------- SMS Compliance (A2P 10DLC) ---------- */
  smsCompliance: {
    brand: null,
    campaign: null,
    phoneAssignments: {
      assignedCount: 0,
      hasAssigned: false,
    },
  },
  smsLoading: false,
  smsError: null,

  /* ---------- Phone Inventory ---------- */
  phoneInventory: [],
  phonePagination: null,
  phoneLoading: false,
  phoneError: null,
  phoneFilters: {
    search: '',
    type: 'all',
    status: 'all',
  },

  /* ---------- Active Tab ---------- */
  activeTab: 'overview', // overview, profile, phones, voice, sms, voip
};

/* =====================================================
   SLICE
   ===================================================== */

const trustHubSlice = createSlice({
  name: "trustHub",
  initialState,

  /* ---------- SYNC REDUCERS ---------- */
  reducers: {
    /**
     * Set active tab
     */
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    },

    /**
     * Set profile status
     */
    setProfileStatus(state, action) {
      state.profileStatus = action.payload;
    },

    /**
     * Update voice compliance
     */
    updateVoiceCompliance(state, action) {
      state.voiceCompliance = {
        ...state.voiceCompliance,
        ...action.payload,
      };
    },

    /**
     * Update SMS compliance
     */
    updateSmsCompliance(state, action) {
      state.smsCompliance = {
        ...state.smsCompliance,
        ...action.payload,
      };
    },

    /**
     * Set phone filters
     */
    setPhoneFilters(state, action) {
      state.phoneFilters = {
        ...state.phoneFilters,
        ...action.payload,
      };
    },

    /**
     * Update single phone in inventory
     */
    updatePhoneInInventory(state, action) {
      const { phoneId, updates } = action.payload;
      const phoneIndex = state.phoneInventory.findIndex(
        (phone) => phone.id === phoneId
      );

      if (phoneIndex !== -1) {
        state.phoneInventory[phoneIndex] = {
          ...state.phoneInventory[phoneIndex],
          ...updates,
        };
      }
    },

    /**
     * Reset Trust Hub state
     */
    resetTrustHub() {
      return { ...initialState };
    },
  },

  /* ---------- ASYNC REDUCERS ---------- */
  extraReducers: (builder) => {
    // Dashboard
    builder
      .addCase(fetchTrustHubDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchTrustHubDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardData = action.payload;
        // Extract and save profile from dashboard data (API returns 'business_profile')
        if (action.payload?.business_profile) {
          state.profile = action.payload.business_profile;
        }
      })
      .addCase(fetchTrustHubDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      });

    // Profile
    builder
      .addCase(fetchTrustHubProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(fetchTrustHubProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profile = action.payload;
      })
      .addCase(fetchTrustHubProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload;
      });

    // Phone Inventory
    builder
      .addCase(fetchPhoneInventory.pending, (state) => {
        state.phoneLoading = true;
        state.phoneError = null;
      })
      .addCase(fetchPhoneInventory.fulfilled, (state, action) => {
        state.phoneLoading = false;
        state.phoneInventory = action.payload.data || [];
        state.phonePagination = {
          current_page: action.payload.current_page,
          last_page: action.payload.last_page,
          per_page: action.payload.per_page,
          total: action.payload.total,
        };
      })
      .addCase(fetchPhoneInventory.rejected, (state, action) => {
        state.phoneLoading = false;
        state.phoneError = action.payload;
      });

    // Voice Compliance
    builder
      .addCase(fetchVoiceCompliance.pending, (state) => {
        state.voiceLoading = true;
        state.voiceError = null;
      })
      .addCase(fetchVoiceCompliance.fulfilled, (state, action) => {
        state.voiceLoading = false;
        state.voiceCompliance = {
          ...state.voiceCompliance,
          ...action.payload,
        };
      })
      .addCase(fetchVoiceCompliance.rejected, (state, action) => {
        state.voiceLoading = false;
        state.voiceError = action.payload;
      });

    // SMS Compliance
    builder
      .addCase(fetchSmsCompliance.pending, (state) => {
        state.smsLoading = true;
        state.smsError = null;
      })
      .addCase(fetchSmsCompliance.fulfilled, (state, action) => {
        state.smsLoading = false;
        state.smsCompliance = action.payload;
      })
      .addCase(fetchSmsCompliance.rejected, (state, action) => {
        state.smsLoading = false;
        state.smsError = action.payload;
      });
  },
});

/* =====================================================
   EXPORTS
   ===================================================== */

export const {
  setActiveTab,
  setProfileStatus,
  updateVoiceCompliance,
  updateSmsCompliance,
  setPhoneFilters,
  updatePhoneInInventory,
  resetTrustHub,
} = trustHubSlice.actions;

export default trustHubSlice.reducer;
